import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Factory, Check, X, RefreshCw, Loader2, Package, Truck, Wrench, ChevronRight, Archive } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

const API = '/api';

const typeConfig = {
  wo_status_update: { icon: Wrench, label: 'Work Order Update', color: 'text-blue-500', bg: 'bg-blue-50 dark:bg-blue-950/20' },
  dispatch_update: { icon: Truck, label: 'Dispatch Update', color: 'text-green-500', bg: 'bg-green-50 dark:bg-green-950/20' },
  production_complete: { icon: Package, label: 'Production Complete', color: 'text-purple-500', bg: 'bg-purple-50 dark:bg-purple-950/20' },
  wo_update: { icon: Wrench, label: 'ERP Update', color: 'text-indigo-500', bg: 'bg-indigo-50 dark:bg-indigo-950/20' },
};

function ActionCard({ action, onApprove, onDismiss, isProcessing }) {
  const config = typeConfig[action.type] || typeConfig.wo_update;
  const Icon = config.icon;

  return (
    <div data-testid={`erp-action-${action.action_id}`} className={cn("p-4 border-b last:border-0", config.bg)}>
      <div className="flex items-start gap-3">
        <div className={cn("p-2 rounded-lg shrink-0", config.color, "bg-white dark:bg-slate-800 border")}>
          <Icon className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-1">
            <span className="font-medium text-sm">{config.label}</span>
            <span className="text-xs text-muted-foreground">{new Date(action.created_at).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' })}</span>
          </div>

          {action.message && <p className="text-sm text-muted-foreground mb-2">{action.message}</p>}

          {/* Product info */}
          <div className="flex flex-wrap gap-2 mb-2">
            {action.lead_info?.kva && <Badge variant="outline" className="text-xs">{action.lead_info.kva} KVA</Badge>}
            {action.lead_info?.phase && <Badge variant="outline" className="text-xs">{action.lead_info.phase}</Badge>}
            {action.lead_info?.model && <Badge variant="outline" className="text-xs">{action.lead_info.model}</Badge>}
            {action.lead_info?.qty && <Badge variant="outline" className="text-xs">Qty: {action.lead_info.qty}</Badge>}
          </div>

          {/* ERP data */}
          {action.erp_data?.wo_number && (
            <div className="text-xs text-muted-foreground mb-1">WO: {action.erp_data.wo_number}</div>
          )}
          {action.erp_data?.wo_status && (
            <div className="text-xs text-muted-foreground mb-1">Status: {action.erp_data.wo_status}</div>
          )}

          {/* Suggested changes */}
          {action.suggested_changes && Object.keys(action.suggested_changes).length > 0 && (
            <div className="mt-2 p-2 bg-white dark:bg-slate-800 rounded border">
              <p className="text-xs font-medium text-muted-foreground mb-1">Suggested Changes:</p>
              {Object.entries(action.suggested_changes).map(([field, value]) => (
                <div key={field} className="flex items-center gap-2 text-xs">
                  <ChevronRight className="h-3 w-3 text-muted-foreground" />
                  <span className="font-medium">{field.replace(/_/g, ' ')}</span>
                  <span className="text-muted-foreground">→</span>
                  <span className="text-primary">{String(value)}</span>
                </div>
              ))}
            </div>
          )}

          {/* Action buttons */}
          {action.status === 'pending' && (
            <div className="flex gap-2 mt-3">
              <Button
                data-testid={`erp-approve-${action.action_id}`}
                size="sm"
                onClick={() => onApprove(action.action_id)}
                disabled={isProcessing}
                className="gap-1"
              >
                {isProcessing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                Approve Change
              </Button>
              <Button
                data-testid={`erp-dismiss-${action.action_id}`}
                size="sm"
                variant="outline"
                onClick={() => onDismiss(action.action_id)}
                disabled={isProcessing}
                className="gap-1"
              >
                <X className="h-3 w-3" />
                Dismiss
              </Button>
            </div>
          )}

          {/* Processed info */}
          {action.status !== 'pending' && (
            <div className="mt-2 text-xs text-muted-foreground">
              {action.status === 'approved' ? 'Approved' : 'Dismissed'} on {new Date(action.processed_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function ERPSyncBell() {
  const [pendingCount, setPendingCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [tab, setTab] = useState('pending');
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [polling, setPolling] = useState(false);
  const [processingId, setProcessingId] = useState(null);

  const fetchCount = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/erp-sync/pending/count`, { withCredentials: true });
      setPendingCount(res.data.count || 0);
    } catch { /* silent */ }
  }, []);

  const fetchActions = useCallback(async (activeTab) => {
    setLoading(true);
    try {
      const endpoint = activeTab === 'pending' ? 'pending' : activeTab === 'dismissed' ? 'dismissed' : 'approved';
      const res = await axios.get(`${API}/erp-sync/${endpoint}`, { withCredentials: true });
      setActions(res.data.actions || []);
    } catch { setActions([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchCount();
    const interval = setInterval(fetchCount, 60000);
    return () => clearInterval(interval);
  }, [fetchCount]);

  useEffect(() => {
    if (isOpen) fetchActions(tab);
  }, [isOpen, tab, fetchActions]);

  const handlePoll = async () => {
    setPolling(true);
    try {
      const res = await axios.post(`${API}/erp-sync/poll`, {}, { withCredentials: true });
      if (res.data.new_actions > 0) {
        toast.success(`${res.data.new_actions} new ERP notification(s) received`);
        fetchActions(tab);
        fetchCount();
      } else {
        toast.info('No new notifications from ERP');
      }
    } catch {
      toast.error('Failed to poll ERP');
    } finally { setPolling(false); }
  };

  const handleApprove = async (actionId) => {
    setProcessingId(actionId);
    try {
      await axios.post(`${API}/erp-sync/approve/${actionId}`, {}, { withCredentials: true });
      toast.success('Changes approved and applied to lead');
      fetchActions(tab);
      fetchCount();
    } catch {
      toast.error('Failed to approve action');
    } finally { setProcessingId(null); }
  };

  const handleDismiss = async (actionId) => {
    setProcessingId(actionId);
    try {
      await axios.post(`${API}/erp-sync/dismiss/${actionId}`, {}, { withCredentials: true });
      toast.success('Notification dismissed');
      fetchActions(tab);
      fetchCount();
    } catch {
      toast.error('Failed to dismiss action');
    } finally { setProcessingId(null); }
  };

  return (
    <>
      <Button
        data-testid="erp-sync-bell"
        variant="ghost"
        size="icon"
        className="relative"
        onClick={() => setIsOpen(true)}
      >
        <Factory className="h-5 w-5" />
        {pendingCount > 0 && (
          <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full flex items-center justify-center text-xs font-bold text-white bg-orange-500 animate-pulse">
            {pendingCount > 99 ? '99+' : pendingCount}
          </span>
        )}
      </Button>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="sm:max-w-[550px] max-h-[80vh] flex flex-col p-0">
          <DialogHeader className="px-5 pt-5 pb-3 shrink-0">
            <DialogTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Factory className="h-5 w-5" />
                ERP Sync
              </span>
              <Button
                data-testid="erp-poll-btn"
                variant="outline"
                size="sm"
                onClick={handlePoll}
                disabled={polling}
                className="gap-1"
              >
                {polling ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                Poll ERP
              </Button>
            </DialogTitle>
          </DialogHeader>

          {/* Tabs */}
          <div className="flex border-b px-5 shrink-0">
            {[
              { id: 'pending', label: 'Pending', count: pendingCount },
              { id: 'approved', label: 'Approved' },
              { id: 'dismissed', label: 'Dismissed' },
            ].map(t => (
              <button
                key={t.id}
                data-testid={`erp-tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={cn(
                  "px-4 py-2.5 text-sm font-medium border-b-2 transition-colors",
                  tab === t.id
                    ? "border-primary text-primary"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                )}
              >
                {t.label}
                {t.count > 0 && (
                  <Badge variant="secondary" className="ml-2 h-5 text-xs">{t.count}</Badge>
                )}
              </button>
            ))}
          </div>

          {/* Content */}
          <ScrollArea className="flex-1" style={{ maxHeight: '50vh' }}>
            {loading ? (
              <div className="p-8 text-center text-muted-foreground">
                <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
                Loading...
              </div>
            ) : actions.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                <Archive className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No {tab} actions</p>
                {tab === 'pending' && (
                  <p className="text-xs mt-1">Click "Poll ERP" to check for new notifications</p>
                )}
              </div>
            ) : (
              actions.map(action => (
                <ActionCard
                  key={action.action_id}
                  action={action}
                  onApprove={handleApprove}
                  onDismiss={handleDismiss}
                  isProcessing={processingId === action.action_id}
                />
              ))
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </>
  );
}
